import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Avatar,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Search,
  FilterList,
  CloudDownload,
  Visibility,
  CalendarToday,
  Person,
  LocationOn,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface DailyReport {
  _id: string;
  project: {
    name: string;
    location: string;
  };
  date: string;
  weather: string;
  temperature: string;
  workDescription: string;
  crewSize: number;
  hoursWorked: number;
  materialsUsed: string[];
  equipmentUsed: string[];
  safetyIncidents: number;
  delays: string;
  notes: string;
  submittedBy: {
    firstName: string;
    lastName: string;
  };
  submittedAt: string;
  status: 'draft' | 'submitted' | 'reviewed';
}

// Helpers to present backend weather shape
const conditionLabels: Record<string, string> = {
  sunny: 'Sunny',
  partly_cloudy: 'Partly Cloudy',
  cloudy: 'Cloudy',
  overcast: 'Overcast',
  light_rain: 'Light Rain',
  heavy_rain: 'Heavy Rain',
  drizzle: 'Drizzle',
  snow: 'Snow',
  sleet: 'Sleet',
  fog: 'Fog',
  windy: 'Windy',
  stormy: 'Stormy',
};

const humanizeCondition = (cond?: string) => {
  if (!cond) return '';
  return conditionLabels[cond] || cond;
};

const formatTemperature = (temp?: { high?: number; low?: number; unit?: 'fahrenheit' | 'celsius' }) => {
  if (!temp || (temp.high == null && temp.low == null)) return '';
  const unit = temp.unit === 'celsius' ? '°C' : '°F';
  if (temp.high != null && temp.low != null) return `${temp.high}${unit} / ${temp.low}${unit}`;
  if (temp.high != null) return `${temp.high}${unit}`;
  if (temp.low != null) return `${temp.low}${unit}`;
  return '';
};

const DailyReports: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState<DailyReport[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const response = await api.get('/daily-reports');
        const apiReports = response?.data?.data || [];
        const mapped = apiReports.map((r: any) => ({
          _id: r?._id,
          project: {
            name: r?.project?.name || 'Project',
            location: r?.project?.location || '',
          },
          date: r?.date || new Date().toISOString(),
          weather: humanizeCondition(r?.weather?.condition) || 'N/A',
          temperature: formatTemperature(r?.weather?.temperature),
          workDescription: r?.workProgress?.summary || r?.notes || 'Daily report',
          crewSize: r?.totalWorkers || 0,
          hoursWorked: r?.totalHours || 0,
          materialsUsed: Array.isArray(r?.materialDeliveries) ? r.materialDeliveries.map((d: any) => d.material).filter(Boolean) : [],
          equipmentUsed: r?.equipmentUsed || [],
          safetyIncidents: Array.isArray(r?.safetyIncidents) ? r.safetyIncidents.length : (r?.issues?.length || 0),
          delays: Array.isArray(r?.delaysOrIssues) ? r.delaysOrIssues.join(', ') : '',
          notes: r?.notes || '',
          submittedBy: {
            firstName: r?.reportedBy?.firstName || '',
            lastName: r?.reportedBy?.lastName || '',
          },
          submittedAt: r?.createdAt || '',
          status: r?.status || 'draft',
        }));
        setReports(mapped);
      } catch (err) {
        console.error('Failed to load daily reports', err);
        setReports([]);
      }
    };
    fetchReports();
  }, []);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, reportId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedReport(reportId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReport(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'reviewed':
        return 'success';
      case 'submitted':
        return 'warning';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const filteredReports = reports.filter(
    (report) =>
      report.project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.submittedBy.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.submittedBy.lastName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          Daily Reports
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ borderRadius: 2 }}
          onClick={() => navigate('/daily-reports/create')}
        >
          New Report
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              placeholder="Search reports..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              sx={{ borderRadius: 2 }}
            >
              Filter
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
        {filteredReports.map((report) => (
          <Box key={report._id}>
            <Card 
              sx={{ 
                height: '100%', 
                borderRadius: 2, 
                boxShadow: 2, 
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}
              onClick={() => navigate(`/daily-reports/${report._id}`)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }}>
                    {report.project.name}
                  </Typography>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuClick(e, report._id);
                    }}
                    size="small"
                  >
                    <MoreVert />
                  </IconButton>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Chip
                    label={report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                    color={getStatusColor(report.status) as any}
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {report.weather} • {report.temperature}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <CalendarToday fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {new Date(report.date).toLocaleDateString()}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {report.project.location}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Person fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {report.submittedBy.firstName} {report.submittedBy.lastName}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="body2" sx={{ mb: 2, minHeight: 40 }}>
                  {report.workDescription}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Crew: {report.crewSize} • Hours: {report.hoursWorked}
                  </Typography>
                  {report.safetyIncidents > 0 && (
                    <Chip
                      label={`${report.safetyIncidents} Incident${report.safetyIncidents > 1 ? 's' : ''}`}
                      color="error"
                      size="small"
                    />
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<Visibility />}
                    variant="outlined"
                    sx={{ flex: 1 }}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    startIcon={<CloudDownload />}
                    variant="outlined"
                    sx={{ flex: 1 }}
                  >
                    Export
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>View Details</MenuItem>
        <MenuItem onClick={handleMenuClose}>Edit Report</MenuItem>
        <MenuItem onClick={handleMenuClose}>Download PDF</MenuItem>
        <MenuItem onClick={handleMenuClose}>Send Email</MenuItem>
        <MenuItem onClick={handleMenuClose}>Archive</MenuItem>
      </Menu>
    </Box>
  );
};

export default DailyReports;